import { Locale } from "../Locale.ts";

/**
 * The fractionBars manipulative's labels (ADR 0117). `{n}` is a bar's number counting down from the
 * whole, `{shaded}` and `{pieces}` its counts, `{a}` and `{b}` fractions written "1/2".
 */
const en = {
    "fractionBars.whole": "1 whole",
    "fractionBars.wholeLabel": "1 whole bar",
    "fractionBars.trackLabel": "Bar {n}: {shaded} of {pieces} equal pieces shaded",
    "fractionBars.fractionLabel": "{shaded} out of {pieces}",
    "fractionBars.piecesHeading": "Pieces",
    "fractionBars.shadedHeading": "Shaded",
    "fractionBars.fewerPieces": "Fewer pieces in bar {n}",
    "fractionBars.morePieces": "More pieces in bar {n}",
    "fractionBars.fewerShaded": "Shade one fewer piece in bar {n}",
    "fractionBars.moreShaded": "Shade one more piece in bar {n}",
    "fractionBars.remove": "Remove bar {n}",
    "fractionBars.add": "Add a bar",
    "fractionBars.reset": "Reset",
    "fractionBars.guide": "Guide line",
    "fractionBars.zero": "= 0",
    "fractionBars.oneWhole": "= 1 whole",
    "fractionBars.simplest": "= {fraction} in simplest form",
    "fractionBars.liveBar": "Bar {n}: {shaded} of {pieces} shaded.",
    "fractionBars.liveEqual": "{a} is equal to {b}.",
    "fractionBars.liveGreater": "{a} is greater than {b}.",
    "fractionBars.liveLess": "{a} is less than {b}.",
} as const;

export type FractionBarsStringKey = keyof typeof en;

const es = {
    "fractionBars.whole": "1 entero",
    "fractionBars.wholeLabel": "Barra de 1 entero",
    "fractionBars.trackLabel": "Barra {n}: {shaded} de {pieces} partes iguales sombreadas",
    "fractionBars.fractionLabel": "{shaded} de {pieces}",
    "fractionBars.piecesHeading": "Partes",
    "fractionBars.shadedHeading": "Sombreadas",
    "fractionBars.fewerPieces": "Menos partes en la barra {n}",
    "fractionBars.morePieces": "Más partes en la barra {n}",
    "fractionBars.fewerShaded": "Sombrear una parte menos en la barra {n}",
    "fractionBars.moreShaded": "Sombrear una parte más en la barra {n}",
    "fractionBars.remove": "Quitar la barra {n}",
    "fractionBars.add": "Añadir una barra",
    "fractionBars.reset": "Reiniciar",
    "fractionBars.guide": "Línea guía",
    "fractionBars.zero": "= 0",
    "fractionBars.oneWhole": "= 1 entero",
    "fractionBars.simplest": "= {fraction} en su forma más simple",
    "fractionBars.liveBar": "Barra {n}: {shaded} de {pieces} sombreadas.",
    "fractionBars.liveEqual": "{a} es igual a {b}.",
    "fractionBars.liveGreater": "{a} es mayor que {b}.",
    "fractionBars.liveLess": "{a} es menor que {b}.",
} satisfies Record<FractionBarsStringKey, string>;

const fr = {
    "fractionBars.whole": "1 entier",
    "fractionBars.wholeLabel": "Barre de 1 entier",
    "fractionBars.trackLabel": "Barre {n} : {shaded} parts égales coloriées sur {pieces}",
    "fractionBars.fractionLabel": "{shaded} sur {pieces}",
    "fractionBars.piecesHeading": "Parts",
    "fractionBars.shadedHeading": "Coloriées",
    "fractionBars.fewerPieces": "Moins de parts dans la barre {n}",
    "fractionBars.morePieces": "Plus de parts dans la barre {n}",
    "fractionBars.fewerShaded": "Colorier une part de moins dans la barre {n}",
    "fractionBars.moreShaded": "Colorier une part de plus dans la barre {n}",
    "fractionBars.remove": "Retirer la barre {n}",
    "fractionBars.add": "Ajouter une barre",
    "fractionBars.reset": "Réinitialiser",
    "fractionBars.guide": "Ligne repère",
    "fractionBars.zero": "= 0",
    "fractionBars.oneWhole": "= 1 entier",
    "fractionBars.simplest": "= {fraction} en forme simplifiée",
    "fractionBars.liveBar": "Barre {n} : {shaded} parts coloriées sur {pieces}.",
    "fractionBars.liveEqual": "{a} est égal à {b}.",
    "fractionBars.liveGreater": "{a} est plus grand que {b}.",
    "fractionBars.liveLess": "{a} est plus petit que {b}.",
} satisfies Record<FractionBarsStringKey, string>;

export const fractionBarsStrings: Record<Locale, Record<FractionBarsStringKey, string>> = { en, es, fr };
